import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Bell, Clock, Minus, Pencil, Plus, Trash2 } from "lucide-react";
import CustomSelect from "../atoms/CustomSelect";
import DarkBlueButton from "../atoms/buttons/DarkBlueButton";
import { useUserData } from "../../lib/contexts/UserContext";
import {
  CommsKind,
  CommsSchedule,
  CommsTemplate,
  MAX_OFFSET_DAYS,
  TIME_STEP_MINUTES,
  createSchedule,
  deleteSchedule,
  describeOffset,
  formatTime12h,
  getSchedules,
  getTemplates,
  updateSchedule,
} from "../../lib/apis/comms";

/**
 * Admin Panel > Patient Communication & Reminders > Scheduled Messages.
 *
 * The comms-engine rule editor: "N days before/after the appointment, at HH:MM,
 * send this text". AdminPanel renders this only while
 * hospital_feature_flags.comms_engine is on, and the API refuses every write
 * with 403 regardless, so the block is never a way around the flag.
 *
 * A rule can be SAVED before Meta approves its template, but not switched on --
 * the API answers 409 and the toggle explains why. That lets a hospital set
 * everything up during the 24-72h review window.
 */

// Type doubles as "which bookings": a reminder goes to upcoming bookings
// (status 0), a follow-up to completed visits (status 3). Coupling them keeps
// the form to one control and stops a follow-up going to a no-show.
const KIND_OPTIONS: {
  value: CommsKind;
  title: string;
  hint: string;
  audience: string;
  statuses: number[];
}[] = [
  {
    value: "reminder",
    title: "Reminder",
    hint: "Patients with an upcoming booking",
    audience: "patients with an upcoming booking",
    statuses: [0],
  },
  {
    value: "followup",
    title: "Follow-up",
    hint: "Patients whose visit is completed",
    audience: "patients whose visit is completed",
    statuses: [3],
  },
];

type Direction = "before" | "same" | "after";

const DAY_PRESETS = [1, 2, 3, 7, 30, 90];
const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MINUTES = Array.from({ length: 60 / TIME_STEP_MINUTES }, (_, i) =>
  String(i * TIME_STEP_MINUTES).padStart(2, "0"),
);
// Matches the API validator: WhatsApp caps the filled-in template body at 1024.
const CONTENT_MAX = 700;

// days_offset <-> (direction, days) for the "When" control.
const splitOffset = (n: number): { direction: Direction; days: number } =>
  n === 0
    ? { direction: "same", days: 1 }
    : { direction: n < 0 ? "before" : "after", days: Math.abs(n) };
const joinOffset = (direction: Direction, days: number) =>
  direction === "same" ? 0 : direction === "before" ? -days : days;
const clampDays = (n: number) =>
  Math.min(MAX_OFFSET_DAYS, Math.max(1, Math.round(n) || 1));

// "HH:MM" (24h) <-> hour / minute / AM-PM pickers.
const splitTime = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return {
    hour12: String(h % 12 === 0 ? 12 : h % 12),
    minute: String(m).padStart(2, "0"),
    pm: h >= 12,
  };
};
const joinTime = (hour12: string, minute: string, pm: boolean) => {
  const h = (Number(hour12) % 12) + (pm ? 12 : 0);
  return `${String(h).padStart(2, "0")}:${minute}`;
};

const autoLabel = (offset: number, atTime: string) =>
  `${describeOffset(offset)} · ${formatTime12h(atTime)}`;

// Same toggle as the rest of the Admin Panel (AdminPanel.tsx toggleButtonClass).
const toggleButtonClass = `group peer bg-white rounded-full duration-300 w-14 h-6 ring-[1.5px] ring-[#A6A6A6]
    after:duration-300 after:bg-[#A6A6A6] peer-checked:after:bg-[#335FE4] peer-checked:ring-[#335FE4]
    after:rounded-full after:absolute after:h-4 after:w-4 after:top-[4px] after:left-1
    after:flex after:justify-center after:items-center peer-checked:after:translate-x-8 peer-hover:after:scale-95`;

const segmentClass = (active: boolean) =>
  `px-3 py-1.5 text-xs md:text-sm rounded-md transition-colors ${
    active
      ? "bg-white text-[#335FE4] font-semibold shadow-sm"
      : "text-gray-600 hover:text-gray-900"
  }`;

const chipClass = (active: boolean) =>
  `px-2.5 py-1 text-xs rounded-full border transition-colors ${
    active
      ? "border-[#335FE4] bg-blue-50 text-[#335FE4] font-medium"
      : "border-gray-200 bg-white text-gray-600 hover:border-gray-400"
  }`;

const selectClass =
  "rounded-lg px-2 py-1.5 border border-gray-300 bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200";

const kindBadgeClass = (kind: CommsKind) =>
  kind === "followup"
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : "text-blue-700 bg-blue-50 border-blue-200";

interface Draft {
  label: string;
  kind: CommsKind;
  days_offset: number;
  at_time: string;
  content_en: string;
  template_key: string;
}

const emptyDraft = (templateKey: string): Draft => ({
  label: "",
  kind: "reminder",
  days_offset: -1,
  at_time: "10:00",
  content_en: "",
  template_key: templateKey,
});

// The fixed parts of the approved comms_reminder_v1 shell, for the preview.
const PreviewToken: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <span className="rounded bg-emerald-100 px-1 text-emerald-800">
    {children}
  </span>
);

interface Props {
  hospitalId: string;
}

const ScheduledMessagesSection: React.FC<Props> = ({ hospitalId }) => {
  const { userData } = useUserData();
  const canEdit = userData?.role === "MasterAdmin";

  const [schedules, setSchedules] = useState<CommsSchedule[]>([]);
  const [templates, setTemplates] = useState<CommsTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [draft, setDraft] = useState<Draft>(emptyDraft(""));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  // Set after a same-slot 409 so the next Save resends with force: true.
  const [forceNext, setForceNext] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // The day count survives a trip through "Same day", which stores 0.
  const [lastDays, setLastDays] = useState(1);

  // Reminders and follow-ups ride utility shells; marketing shells are for ads.
  const utilityTemplates = useMemo(
    () => templates.filter((t) => t.category === "utility"),
    [templates],
  );
  const templateByKey = useMemo(
    () => new Map(templates.map((t) => [t.template_key, t])),
    [templates],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [s, t] = await Promise.all([
        getSchedules(hospitalId),
        getTemplates(hospitalId),
      ]);
      setSchedules(s?.data?.result?.schedules ?? []);
      setTemplates(t?.data?.result?.templates ?? []);
    } catch (error) {
      console.error("Error loading scheduled messages:", error);
      toast.error("Could not load scheduled messages.");
    } finally {
      setIsLoading(false);
    }
  }, [hospitalId]);

  useEffect(() => {
    load();
  }, [load]);

  // Default the picker to the first utility shell once templates arrive, so a
  // hospital with one template never has to choose it.
  useEffect(() => {
    if (!draft.template_key && utilityTemplates.length) {
      setDraft((d) => ({
        ...d,
        template_key: utilityTemplates[0].template_key,
      }));
    }
  }, [utilityTemplates, draft.template_key]);

  /**
   * Surface what the API actually said. Error helpers put text in `error`
   * (express-validator: an ARRAY of { msg }); the hand-rolled 403 uses
   * `message`. Same handling as AppointmentPrograms.
   */
  const apiError = async (error: any, fallback: string) => {
    const data = error?.response?.data;
    const detail = Array.isArray(data?.error)
      ? data.error
          .map((e: any) => e?.msg || e?.message)
          .filter(Boolean)
          .join(". ")
      : data?.error || data?.message;
    toast.error(detail || fallback);
    if (error?.response?.status === 403) await load();
  };

  const openCreate = () => {
    setEditingId(null);
    setDraft(emptyDraft(utilityTemplates[0]?.template_key ?? ""));
    setLastDays(1);
    setForceNext(false);
    setShowForm(true);
  };

  const openEdit = (rule: CommsSchedule) => {
    setEditingId(rule.comms_schedule_id);
    setDraft({
      label: rule.label,
      kind: rule.kind,
      days_offset: rule.days_offset,
      at_time: rule.at_time,
      content_en: rule.content.en ?? "",
      template_key: rule.template_key,
    });
    setLastDays(splitOffset(rule.days_offset).days);
    setForceNext(false);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForceNext(false);
  };

  const handleSave = async () => {
    if (!draft.content_en.trim()) return toast.error("Enter the message text.");
    if (!draft.template_key)
      return toast.error("No message template is set up yet.");

    const kindOpt =
      KIND_OPTIONS.find((k) => k.value === draft.kind) ?? KIND_OPTIONS[0];
    const body = {
      // An empty name falls back to the timing, which is what admins type anyway.
      label: draft.label.trim() || autoLabel(draft.days_offset, draft.at_time),
      kind: draft.kind,
      days_offset: draft.days_offset,
      at_time: draft.at_time,
      template_key: draft.template_key,
      content: { en: draft.content_en.trim() },
      booking_statuses: kindOpt.statuses,
      force: forceNext || undefined,
    };

    setIsSaving(true);
    try {
      if (editingId) {
        await updateSchedule(hospitalId, editingId, body);
        toast.success("Scheduled message updated.");
      } else {
        await createSchedule(hospitalId, body);
        toast.success(
          "Scheduled message added. Switch it on when you're ready.",
        );
      }
      closeForm();
      await load();
    } catch (error: any) {
      // A same-slot clash comes back 409 mentioning force; one more Save adds it.
      const msg: string = error?.response?.data?.error || "";
      if (error?.response?.status === 409 && /force/i.test(msg)) {
        setForceNext(true);
        toast.warn(
          "Another message already fires at that time. Click Save again to add this one too.",
        );
      } else {
        await apiError(error, "Could not save the scheduled message.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async (rule: CommsSchedule) => {
    setIsSaving(true);
    try {
      await updateSchedule(hospitalId, rule.comms_schedule_id, {
        enabled: !rule.enabled,
      });
      toast.success(`${rule.label} switched ${rule.enabled ? "off" : "on"}.`);
      await load();
    } catch (error) {
      await apiError(error, "Could not change the message.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (rule: CommsSchedule) => {
    setIsSaving(true);
    try {
      await deleteSchedule(hospitalId, rule.comms_schedule_id);
      toast.success(`${rule.label} deleted.`);
      setConfirmDeleteId(null);
      if (editingId === rule.comms_schedule_id) closeForm();
      await load();
    } catch (error) {
      await apiError(error, "Could not delete the message.");
    } finally {
      setIsSaving(false);
    }
  };

  // Only a missing template is worth showing: languages are not listed, because
  // the backend sends in the patient's language and falls back to English when
  // that language isn't approved on the hospital's number.
  const templateMissing = (templateKey: string) =>
    !templateByKey.has(templateKey);

  const enabledCount = schedules.filter((s) => s.enabled).length;

  // The list reads as a timeline around the appointment.
  const groups = useMemo(() => {
    const sorted = [...schedules].sort(
      (a, b) =>
        a.days_offset - b.days_offset || a.at_time.localeCompare(b.at_time),
    );
    return [
      {
        title: "Before the appointment",
        rules: sorted.filter((r) => r.days_offset < 0),
      },
      {
        title: "On the appointment day",
        rules: sorted.filter((r) => r.days_offset === 0),
      },
      {
        title: "After the appointment",
        rules: sorted.filter((r) => r.days_offset > 0),
      },
    ].filter((g) => g.rules.length);
  }, [schedules]);

  // ----- form helpers -----
  // Timing decides the type: before = reminder (upcoming bookings), after =
  // follow-up (completed visits). Only "On the day" asks which one.
  const { direction, days } = splitOffset(draft.days_offset);
  const time = splitTime(draft.at_time);
  const kindOpt =
    KIND_OPTIONS.find((k) => k.value === draft.kind) ?? KIND_OPTIONS[0];

  const setDirection = (d: Direction) => {
    const kind: CommsKind =
      d === "before" ? "reminder" : d === "after" ? "followup" : draft.kind;
    setDraft({ ...draft, kind, days_offset: joinOffset(d, lastDays) });
  };
  const setDays = (n: number) => {
    const v = clampDays(n);
    setLastDays(v);
    setDraft({ ...draft, days_offset: joinOffset(direction, v) });
  };
  const setTime = (patch: Partial<ReturnType<typeof splitTime>>) => {
    const t = { ...time, ...patch };
    setDraft({ ...draft, at_time: joinTime(t.hour12, t.minute, t.pm) });
  };

  const whenSentence =
    direction === "same"
      ? `on the day of the appointment at ${formatTime12h(draft.at_time)}`
      : `${days} day${days === 1 ? "" : "s"} ${direction} the appointment, at ${formatTime12h(draft.at_time)}`;

  return (
    <div className="flex flex-col mt-4 p-2 md:p-4">
      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3">
        <div className="flex flex-col mt-2">
          <h1 className="text-sm md:text-md font-semibold">
            Scheduled Messages
            <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
              New
            </span>
          </h1>
          <p className="text-xs md:text-sm text-gray-600">
            Send your own WhatsApp reminders and follow-ups, up to{" "}
            {MAX_OFFSET_DAYS} days before or after the appointment.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Times are in your hospital's timezone. Each patient gets a message
            once per booking.
          </p>
        </div>
        <div className="flex items-center gap-3 md:mt-2 shrink-0">
          <span className="text-xs md:text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{enabledCount}</span>{" "}
            on · {schedules.length} total
          </span>
          {canEdit && !showForm && (
            <DarkBlueButton
              name="Add Message"
              disabled={isSaving}
              handleClick={openCreate}
            />
          )}
        </div>
      </div>

      {showForm && canEdit && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div className="text-sm font-semibold text-gray-900">
              {editingId ? "Edit message" : "New message"}
            </div>
            <button
              className="text-xs text-gray-500 hover:text-gray-800"
              onClick={closeForm}
              disabled={isSaving}
            >
              Close
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 p-4">
            {/* ---------- left: the settings ---------- */}
            <div className="lg:col-span-3 flex flex-col gap-5">
              {/* Name */}
              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-gray-800">
                  Name{" "}
                  <span className="font-normal text-gray-400">
                    (optional, only staff see it)
                  </span>
                </span>
                <input
                  className="rounded-lg px-3 py-2 border border-gray-300 bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder={autoLabel(draft.days_offset, draft.at_time)}
                  value={draft.label}
                  maxLength={100}
                  onChange={(e) =>
                    setDraft({ ...draft, label: e.target.value })
                  }
                />
              </label>

              {/* When */}
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-gray-800">Send</span>
                <div className="flex w-fit rounded-lg bg-gray-100 p-1">
                  {(["before", "same", "after"] as Direction[]).map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={segmentClass(direction === d)}
                      onClick={() => setDirection(d)}
                    >
                      {d === "before"
                        ? "Before the appointment"
                        : d === "same"
                          ? "On the day"
                          : "After the appointment"}
                    </button>
                  ))}
                </div>

                {direction === "same" ? (
                  <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                    to
                    <div className="flex rounded-lg bg-gray-100 p-0.5">
                      {KIND_OPTIONS.map((k) => (
                        <button
                          key={k.value}
                          type="button"
                          className={segmentClass(draft.kind === k.value)}
                          onClick={() => setDraft({ ...draft, kind: k.value })}
                        >
                          {k.hint}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center rounded-lg border border-gray-300 bg-white">
                      <button
                        type="button"
                        className="px-2 py-1.5 text-gray-500 hover:text-gray-900 disabled:opacity-30"
                        onClick={() => setDays(days - 1)}
                        disabled={days <= 1}
                        aria-label="One day less"
                      >
                        <Minus size={14} />
                      </button>
                      <input
                        type="number"
                        min={1}
                        max={MAX_OFFSET_DAYS}
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="w-12 text-center text-sm font-semibold text-gray-900 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        aria-label="Number of days"
                      />
                      <button
                        type="button"
                        className="px-2 py-1.5 text-gray-500 hover:text-gray-900 disabled:opacity-30"
                        onClick={() => setDays(days + 1)}
                        disabled={days >= MAX_OFFSET_DAYS}
                        aria-label="One day more"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <span className="text-sm text-gray-600">
                      day{days === 1 ? "" : "s"} {direction}
                    </span>
                    <span className="mx-1 h-5 w-px bg-gray-200" />
                    {DAY_PRESETS.map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={chipClass(days === n)}
                        onClick={() => setDays(n)}
                      >
                        {n}d
                      </button>
                    ))}
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  {kindOpt.title}: goes only to {kindOpt.audience}.
                </p>
                {direction === "same" && draft.kind === "followup" && (
                  <p className="text-xs text-amber-700">
                    Only visits already marked completed by the send time get
                    this. Pick a time after the clinic closes (e.g. 8:00 PM).
                  </p>
                )}
              </div>

              {/* Time */}
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-gray-800">At</span>
                <div className="flex flex-wrap items-center gap-2">
                  <Clock size={16} className="text-gray-400" />
                  <select
                    className={selectClass}
                    value={time.hour12}
                    onChange={(e) => setTime({ hour12: e.target.value })}
                    aria-label="Hour"
                  >
                    {HOURS_12.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                  <span className="text-gray-500">:</span>
                  <select
                    className={selectClass}
                    value={time.minute}
                    onChange={(e) => setTime({ minute: e.target.value })}
                    aria-label="Minute"
                  >
                    {MINUTES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <div className="flex rounded-lg bg-gray-100 p-0.5">
                    <button
                      type="button"
                      className={segmentClass(!time.pm)}
                      onClick={() => setTime({ pm: false })}
                    >
                      AM
                    </button>
                    <button
                      type="button"
                      className={segmentClass(time.pm)}
                      onClick={() => setTime({ pm: true })}
                    >
                      PM
                    </button>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-gray-800">
                  Message
                </span>
                <textarea
                  className="rounded-lg px-3 py-2 border border-gray-300 bg-white text-sm text-gray-900 resize-y min-h-[88px] focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="e.g. Please bring your previous reports and arrive 10 minutes early."
                  value={draft.content_en}
                  maxLength={CONTENT_MAX}
                  // Line breaks are rejected by WhatsApp at send time, so they are
                  // folded into spaces here rather than failing later for every patient.
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      content_en: e.target.value.replace(/[\r\n\t]+/g, " "),
                    })
                  }
                />
                <div className="flex justify-between text-[11px] text-gray-500">
                  <span>
                    One paragraph: WhatsApp templates can't contain line breaks.
                  </span>
                  <span
                    className={
                      draft.content_en.length > CONTENT_MAX - 50
                        ? "text-amber-600"
                        : ""
                    }
                  >
                    {draft.content_en.length}/{CONTENT_MAX}
                  </span>
                </div>
              </div>

              {utilityTemplates.length > 1 && (
                <label className="flex flex-col gap-1 text-xs text-gray-600 md:w-1/2">
                  Template
                  <CustomSelect
                    className="!w-full"
                    options={utilityTemplates.map((t) => t.template_key)}
                    value={draft.template_key}
                    onChange={(key) =>
                      setDraft({ ...draft, template_key: key })
                    }
                  />
                </label>
              )}
            </div>

            {/* ---------- right: what the patient will get ---------- */}
            <div className="lg:col-span-2 flex flex-col gap-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Preview
              </span>
              <div className="rounded-xl bg-[#efeae2] p-3">
                <div className="ml-auto max-w-[95%] rounded-lg rounded-tr-none bg-[#d9fdd3] px-3 py-2 text-[13px] leading-relaxed text-gray-900 shadow-sm">
                  Hello <PreviewToken>patient name</PreviewToken>, This is a
                  message from <PreviewToken>hospital</PreviewToken> regarding
                  your appointment with <PreviewToken>doctor</PreviewToken> on{" "}
                  <PreviewToken>date &amp; time</PreviewToken>:{" "}
                  {draft.content_en.trim() ? (
                    <span className="font-medium">
                      {draft.content_en.trim()}
                    </span>
                  ) : (
                    <span className="italic text-gray-400">
                      your message appears here
                    </span>
                  )}{" "}
                  For any changes or questions, please contact the hospital
                  directly. Thank you.
                  <div className="mt-1 text-right text-[10px] text-gray-500">
                    {formatTime12h(draft.at_time)}
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                Goes to{" "}
                <span className="font-semibold">{kindOpt.audience}</span>,{" "}
                <span className="font-semibold">{whenSentence}</span>.
              </div>
            </div>
          </div>

          {!utilityTemplates.length && !isLoading && (
            <p className="px-4 pb-2 text-xs text-red-600">
              No WhatsApp template is registered for scheduled messages yet.
            </p>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-4 py-3">
            <button
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-100"
              onClick={closeForm}
              disabled={isSaving}
            >
              Cancel
            </button>
            <DarkBlueButton
              name={
                forceNext
                  ? "Save anyway"
                  : editingId
                    ? "Save changes"
                    : "Save message"
              }
              disabled={isSaving || !utilityTemplates.length}
              handleClick={handleSave}
            />
          </div>
        </div>
      )}

      <div className="mt-4">
        {isLoading ? (
          <p className="text-xs md:text-sm text-gray-500">Loading…</p>
        ) : schedules.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center">
            <Bell size={22} className="mx-auto text-gray-300" />
            <p className="mt-2 text-sm text-gray-600">
              No scheduled messages yet.
            </p>
            {canEdit && (
              <p className="text-xs text-gray-500">
                Click "Add Message" to create the first one.
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {groups.map((group) => (
              <div key={group.title} className="flex flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {group.title}
                </span>
                <div className="flex flex-col divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
                  {group.rules.map((rule) => (
                    <div
                      key={rule.comms_schedule_id}
                      className={`flex flex-col md:flex-row md:items-center gap-3 px-3 py-3 ${
                        editingId === rule.comms_schedule_id
                          ? "bg-blue-50/50"
                          : ""
                      }`}
                    >
                      {/* timing block */}
                      <div
                        className={`flex md:flex-col md:w-28 shrink-0 items-baseline md:items-start gap-2 md:gap-0 rounded-lg px-2.5 py-1.5 ${
                          rule.enabled ? "bg-gray-50" : "bg-gray-50 opacity-60"
                        }`}
                      >
                        <span className="text-sm font-semibold text-gray-900">
                          {describeOffset(rule.days_offset)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatTime12h(rule.at_time)}
                        </span>
                      </div>

                      <div
                        className={`flex flex-col min-w-0 flex-1 ${rule.enabled ? "" : "opacity-60"}`}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900">
                            {rule.label}
                          </span>
                          <span
                            className={`text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 ${kindBadgeClass(rule.kind)}`}
                          >
                            {rule.kind === "followup" ? "follow-up" : rule.kind}
                          </span>
                        </div>
                        <p
                          className="text-xs text-gray-600 mt-1 line-clamp-2"
                          title={rule.content.en}
                        >
                          {rule.content.en}
                        </p>
                        {templateMissing(rule.template_key) && (
                          <div className="mt-1 text-xs text-red-600">
                            template missing
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {canEdit &&
                          (confirmDeleteId === rule.comms_schedule_id ? (
                            <>
                              <span className="text-xs text-gray-600">
                                Delete?
                              </span>
                              <button
                                className="px-2.5 py-1 text-xs rounded-lg bg-red-600 text-white disabled:opacity-50"
                                disabled={isSaving}
                                onClick={() => handleDelete(rule)}
                              >
                                Delete
                              </button>
                              <button
                                className="px-2.5 py-1 text-xs rounded-lg border border-gray-300"
                                onClick={() => setConfirmDeleteId(null)}
                              >
                                Keep
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                                disabled={isSaving}
                                onClick={() => openEdit(rule)}
                                title="Edit"
                                aria-label={`Edit ${rule.label}`}
                              >
                                <Pencil size={15} />
                              </button>
                              <button
                                className="p-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600"
                                disabled={isSaving}
                                onClick={() =>
                                  setConfirmDeleteId(rule.comms_schedule_id)
                                }
                                title="Delete"
                                aria-label={`Delete ${rule.label}`}
                              >
                                <Trash2 size={15} />
                              </button>
                            </>
                          ))}
                        <span
                          className={`ml-2 w-6 text-right text-xs font-medium ${
                            rule.enabled ? "text-[#335FE4]" : "text-gray-400"
                          }`}
                        >
                          {rule.enabled ? "On" : "Off"}
                        </span>
                        <label
                          className={`flex relative ${canEdit ? "cursor-pointer" : "cursor-not-allowed"}`}
                          title={rule.enabled ? "Switch off" : "Switch on"}
                        >
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={rule.enabled}
                            disabled={!canEdit || isSaving}
                            onChange={() => handleToggle(rule)}
                          />
                          <div className={toggleButtonClass} />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduledMessagesSection;
